import moment from 'moment';
import { FILTER_FIELDS, RELATIONSHIP_KEYS, AGING_KEYS } from './filterConstants';
import { IOptionProps } from '../../../admin/manageRequests/models';
import { StringConstants, appInsightsService } from 'app/utils/constants';

export interface BuildFilterParams {
  searchLink: string;
  statusSelection: IOptionProps[];
  agingSelection: IOptionProps[];
  productsSelection: IOptionProps[];
  countrySelection: IOptionProps[];
  relationshipSelection: IOptionProps[];
}

export enum SearchOptionExternal {
  Article = 'Article',
  HelpRequest = 'HelpRequest',
  Default = 'Default'
}

/**
 * Builds a filter string for querying user search results based on multiple selection criteria.
 *
 * @param params - The filter parameters.
 * @param params.searchLink - The current search context, used to determine applicable filters.
 * @param params.statusSelection - Array of selected status options.
 * @param params.agingSelection - Array of selected aging options (e.g., last 7 days, last 30 days).
 * @param params.productsSelection - Array of selected product options.
 * @param params.countrySelection - Array of selected country options.
 * @param params.relationshipSelection - Array of selected relationship options.
 * @returns A filter string composed of logical expressions for the selected criteria, or `undefined` if no filters are applied.
 */
export const buildFilterString = ({
  searchLink,
  statusSelection,
  agingSelection,
  productsSelection,
  countrySelection,
  relationshipSelection
}: BuildFilterParams): string | undefined => {
  const expressions: string[] = [];
  // const isTicketContext = searchLink === SearchOptionExternal.HelpRequest || searchLink === SearchOptionExternal.Default;

  const agingExpr = (() => {
    if (agingSelection.length === 0 || agingSelection.some(a => a.key === AGING_KEYS.all)) return undefined;
    const rangeClauses: string[] = agingSelection.map(a => {
      switch (a.key) {
        case AGING_KEYS.sevenDays: {
          const start = moment().subtract(7, 'days').startOf('day').toISOString();
          return `(${FILTER_FIELDS.createdOn} ge ${start})`;
        }
        case AGING_KEYS.thirtyDays: {
          const start = moment().subtract(30, 'days').startOf('day').toISOString();
          const end = moment().subtract(8, 'days').endOf('day').toISOString();
          return `(${FILTER_FIELDS.createdOn} ge ${start} and ${FILTER_FIELDS.createdOn} le ${end})`;
        }
        case AGING_KEYS.thirtyOnePlus: {
          const end = moment().subtract(31, 'days').endOf('day').toISOString();
          return `(${FILTER_FIELDS.createdOn} le ${end})`;
        }
        default:
          return '';
      }
    }).filter(Boolean);
    return rangeClauses.length ? `(${rangeClauses.join(' or ')})` : undefined;
  })();

  if (statusSelection.length > 0 && statusSelection.every(s => s.key !== StringConstants.ALL)) {
    const statusValues: number[] = [];
    statusSelection.forEach(s => {
      const raw = (s as IOptionProps).type;
      if (Array.isArray(raw)) {
        raw.forEach(val => {
          const num = typeof val === 'number' ? val : parseInt(String(val), 10);
          if (!isNaN(num)) statusValues.push(num);
        });
      } else if (raw !== undefined) {
        const num = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
        if (!isNaN(num)) statusValues.push(num);
      }
    });
    const distinct = Array.from(new Set(statusValues));
    if (distinct.length) {
      const statusOr = distinct.map(v => `${FILTER_FIELDS.status} eq ${v}`).join(' or ');
      expressions.push(`(${statusOr})`);
      appInsightsService.logEvent('Search Filter Added', {
        column: FILTER_FIELDS.status,
        values: distinct.map(v => v.toString()).join(',')
      });
    }
  }
  if (productsSelection.length > 0 && productsSelection.every(p => p.key !== 0)) {
    const prodOr = productsSelection
      .map(p => `${FILTER_FIELDS.deviceModels}/any(d: d/${FILTER_FIELDS.deviceModelName} eq '${p.text.replace(/'/g, "''")}')`)
      .join(' or ');
    expressions.push(`(${prodOr})`);
    appInsightsService.logEvent('Search Filter Added', {
      column: FILTER_FIELDS.deviceModels,
      values: productsSelection.map(p => String(p.text)).join(',')
    });
  }
  if (countrySelection.length > 0 && countrySelection.every(c => c.key !== 0)) {
    const countryOr = countrySelection
      .map(c => `${FILTER_FIELDS.customerImpacts}/any(ci: ci/${FILTER_FIELDS.customerCountry}/${FILTER_FIELDS.countryName} eq '${c.text.replace(/'/g, "''")}')`)
      .join(' or ');
    expressions.push(`(${countryOr})`);
    appInsightsService.logEvent('Search Filter Added', {
      column: FILTER_FIELDS.customerImpacts,
      values: countrySelection.map(c => String(c.text)).join(',')
    });
  }
  if (relationshipSelection.length > 0 && relationshipSelection.every(r => r.key !== RELATIONSHIP_KEYS.all)) {
    const mapped = relationshipSelection
      .map(r => {
        const k = (r.key || '').toString().toLowerCase();
        if (k === RELATIONSHIP_KEYS.parent) return `${FILTER_FIELDS.isParent} eq true`;
        if (k === RELATIONSHIP_KEYS.child) return `${FILTER_FIELDS.isParent} eq false`;
        return '';
      })
      .filter(Boolean);
    if (mapped.length === 1) expressions.push(mapped[0]);
    else if (mapped.length > 1) expressions.push(`(${mapped.join(' or ')})`);
    if (mapped.length) {
      appInsightsService.logEvent('Search Filter Added', {
        column: FILTER_FIELDS.isParent,
        values: mapped.join('|')
      });
    }
  }

  if (agingExpr) {
    expressions.push(agingExpr);
    appInsightsService.logEvent('Search Filter Added', {
      column: FILTER_FIELDS.createdOn,
      values: agingSelection.map(a => String(a.key)).join(',')
    });
  }
  return expressions.length ? expressions.join(' and ') : undefined;
};
